import styled from 'styled-components';
import whiteCheckMark from 'assets/images/check-mark-white.svg';

const Table = styled.table`
  width: 100%;
  display: table;
  border-spacing: 0;
  thead {
    border-collapse: collapse;
  }
  th {
    font-weight: normal;
    color: ${p => p.theme.color.label};
    max-width: 160px;
    padding: 17px 0px;
    font-size: 14px;
    border-collapse: collapse;
    background-color: #f5f5f9;
  }

  tr th,
  tr td {
    border-right: 1px solid ${p => p.theme.color.border};
    border-bottom: 1px solid ${p => p.theme.color.border};
    text-align: center;
  }
  tr th:first-child,
  tr td:first-child {
    border-left: 1px solid ${p => p.theme.color.border};
  }
  tr th {
    border-top: 1px solid ${p => p.theme.color.border};
  }

  /* top-left border-radius */
  tr:first-child th:first-child {
    border-top-left-radius: 6px;
  }

  /* top-right border-radius */
  tr:first-child th:last-child {
    border-top-right-radius: 6px;
  }

  /* bottom-left border-radius */
  tr:last-child td:first-child {
    border-bottom-left-radius: 6px;
  }

  /* bottom-right border-radius */
  tr:last-child td:last-child {
    border-bottom-right-radius: 6px;
  }
`;

const TableRow = styled.tr`
  vertical-align: middle;
  border-collapse: collapse;
  transition: background-color 1s;
  td {
    padding: 0em 1em;
    box-sizing: border-box;
    position: relative;
    &:hover:not(:first-of-type){
      cursor: pointer;
    }
  }
  td div {
    color: ${p => p.theme.color.label};
  }
  .triggers {
    text-align: center;
    width: 3%;
  }
  .time {
    width: 5%;
    padding: 0;
    position: relative;
    .delay-timer {
      bottom: -0.2em;
    }
  }
  .patient {
    min-width: 170px;
    width: 30%;
  }
  .ap-type {
    width: 8%;
  }
  .note {
    text-align: left;
    width: 30%;
    padding: 0 1em;
  } 
  
  /* booked */
  &.booked {
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* arrived */
  &.arrived {
    background-color: ${p => p.theme.color.veryLightBlue};
    .time-line {
      background-color: #6aa6eb;
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* in-progress */
  &.in-progress {
    background-color: ${p => p.theme.color.veryLightRed};
    .time-line {
      background-color: #ef5350;
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* to-radiology */
  &.to-radiology {
    background-color: ${p => p.theme.color.veryLightPurple};
    .time-line {
      background-color: ${p => p.theme.color.purple};
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* from-radiology */
  &.from-radiology {
    background-color: ${p => p.theme.color.veryLightBlue};
    .time-line {
      background-color: #6aa6eb;
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* restart-consultation */
  &.restart-consultation {
    background-color: ${p => p.theme.color.veryLightGold};
    .time-line {
      background-color: ${p => p.theme.color.gold};
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* finished */
  &.finished {
    background-color: ${p => p.theme.color.veryLightGreen};
    .time-line {
      background-color: #3accac;
    }
    td div {
      color: ${p => p.theme.color.text};
    }
  }
  /* paid */
  &.paid {
    .time-line {
      background-color: #e9eaed;
    }
    td div:not(.personal-info) {
      text-decoration: line-through;
      color: ${p => p.theme.color.label};
    }
  }
  /* canceled */
  &.cancelled {
    td div {
      color: ${p => p.theme.color.error};
    }
    .time-line + div {
      color: ${p => p.theme.color.text};
    }
  }
  td div.personal-info {
    color: ${p => p.theme.color.label};
  }
  &.updated-status {
    background-color: #fff5e3;
  }

  ${p => p.active && `
    background-color: ${p.theme.color.veryLightBlue};    
    td div {
      color: ${p.theme.color.text};
    }`
}
`;

const CellText = styled.div`
  font-size: 14px;
  text-decoration: ${p => p.isFinished && 'line-through'};
  color: ${p => p.isFinished && p.theme.color.label};
`;

const checkBoxBorderColor = p => {
  let color = p.theme.color.border;
  if (p.checked || p.halfChecked) {
    color = p.theme.color.primary;
  }
  if (p.error) {
    color = p.theme.color.error;
  }
  return color;
};

const CheckBoxEl = styled.div`
  width: 18px;
  height: 18px;
  display: inline-block;
  position: relative;
  border-radius: 3px;
  border: 1px solid;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  border-color: ${p => checkBoxBorderColor(p)};
  background-color: ${p => {
    if (p.checked || p.halfChecked) {
      return p.theme.color.primary;
    } if (p.disabled) {
      return p.theme.color.border;
    } else {
      return 'white';
    }
  }};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: 80%;
  ${p => { if (p.checked || p.disabled) return `background-image: url(${whiteCheckMark});`; }};
  ${p => p.halfChecked && `&:after{
    content: '';
    width: 10px;
    height: 2px;
    background-color: white;
    position: absolute;
    top: 45%;
    left: 3px;
    display: block;
  }`};
  &:hover{
    cursor: pointer;
    opacity: .8;
  }
`;


export default {
  CheckBoxEl,
  CellText,
  Table,
  TableRow,
};